import {
	RiBracesLine,
	RiDownload2Line,
	RiErrorWarningLine,
	RiFileTextLine,
	RiFilter3Line,
	RiGroupLine,
	RiKey2Line,
	RiRocket2Line,
	RiShieldKeyholeLine,
	RiSpeedLine,
} from '@remixicon/react';
import { type ReactNode, useMemo } from 'react';

import { useDocuments } from '@shared/docs/provider';
import type { DocumentPage } from '@shared/docs/types';
import Link from '@shared/router/link';

import { MethodBadge } from '@portal/views/_shared/method-badge';

import css from './styles.module.css';

const ReferenceRoot = '/reference';

/** Where a page that names no `order` in its frontmatter sorts. */
const Unordered = Number.MAX_SAFE_INTEGER;

/** Collects the pages that name no `group`. */
const OtherGroup = 'Other';

/** The OpenAPI document papidocs writes beside the reference pages. */
const SpecUrl = '/reference/openapi.json';

/** Keyed by the guide's own segment, so a renamed file loses its icon loudly. */
const icons: Record<string, ReactNode> = {
	'getting-started': <RiRocket2Line />,
	authentication: <RiShieldKeyholeLine />,
	'groups-and-scopes': <RiGroupLine />,
	keys: <RiKey2Line />,
	'pagination-and-filtering': <RiFilter3Line />,
	'rate-limits': <RiSpeedLine />,
	errors: <RiErrorWarningLine />,
};

interface PageGroup {
	label: string;
	pages: DocumentPage[];
}

function weight(page: DocumentPage): number {
	return Number(page.frontmatter['order'] ?? Unordered);
}

function byWeight(first: DocumentPage, second: DocumentPage): number {
	return weight(first) - weight(second);
}

/**
 *	Pages grouped by their frontmatter `group`. A group sorts by the lowest
 *	`order` among its pages, and its pages by their own `order`.
 */
function toGroups(pages: DocumentPage[]): PageGroup[] {
	const groups: Map<string, DocumentPage[]> = new Map();

	for (const page of pages.toSorted(byWeight)) {
		const label = page.frontmatter['group'] ?? OtherGroup;

		groups.set(label, [...(groups.get(label) ?? []), page]);
	}

	return [...groups].map(([label, grouped]) => ({ label, pages: grouped }));
}

/** The pages under one collection root, its own index page excluded. */
function useCollection(root: string): DocumentPage[] {
	const { manifest } = useDocuments();

	return useMemo(
		() => manifest.pages.filter((page) => page.url.startsWith(`${root}/`)),
		[manifest.pages, root]
	);
}

function useIndexPage(root: string): Optional<DocumentPage> {
	const { manifest } = useDocuments();

	return manifest.pages.find((page) => page.url === root);
}

interface DocsNavigationProps {
	/** The collection the page in view belongs to: `/docs` or `/reference`. */
	root: string;
}

export function DocsNavigation({ root }: DocsNavigationProps): ReactNode {
	const pages = useCollection(root);
	const index = useIndexPage(root);

	const groups = useMemo(() => toGroups(pages), [pages]);

	return (
		<menu className={css.wrapper}>
			{root === ReferenceRoot && (
				<section className={css.group}>
					<ul>
						{index ? (
							<li>
								<Link href={index.url} className={css.item}>
									<RiBracesLine />
									{index.title}
								</Link>
							</li>
						) : null}
						<li>
							{/* A file rather than a route, so a plain anchor: <Link> would
							    hand the click to the router, which has nothing to show for
							    it. The portal serves it out of the reference directory —
							    see src/index.ts and src/bin/build.ts. */}
							<a className={css.item} href={SpecUrl} download>
								<RiDownload2Line />
								Download OpenAPI Schema
							</a>
						</li>
					</ul>
				</section>
			)}

			{groups.map((group) => (
				<section key={group.label} className={css.group}>
					<h4>{group.label}</h4>
					<ul>
						{group.pages.map((page) => (
							<li key={page.url}>
								<Link href={page.url} className={css.item}>
									<PageMarker page={page} />
									{page.title}
								</Link>
							</li>
						))}
					</ul>
				</section>
			))}
		</menu>
	);
}

/** A reference page leads with its method, a guide with its icon. */
function PageMarker({ page }: { page: DocumentPage }): ReactNode {
	const method = page.frontmatter['method'];

	if (method) {
		return <MethodBadge method={method} />;
	}

	return icons[page.segments.at(-1) ?? ''] ?? <RiFileTextLine />;
}
